import { useCallback, useEffect, useState } from 'react'
import { Content as ContentType, NLGAction } from '@phrasee/phrasee-typings'
import { useQueryClient } from '@tanstack/react-query'

import { errorToast } from 'common/components/toastNotification'
import { stringToNumber } from 'common/helpers/numeric'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import useWebsockets, { WsResponseBase } from 'common/hooks/useWebsockets'
import { contentKeys } from 'features/unifiedFlow/api/queryKeys'
import { useContent } from 'features/unifiedFlow/contentPage/hooks'
import {
  selectElement,
  toggleElementsDisabled,
} from 'features/unifiedFlow/store/unifiedFlowSlice'

import { BRIEF_ELEMENT_ID } from '../components/universalBriefElement/constants'
import { getRootUserSelectedElement } from '../helpers'

import { getWsTopic } from './websocketMessage/helpers'
import { getFullscreenGenerationWait } from './getFullscreenGenerationWait'
import useWebsocketMessage from './websocketMessage'
import { useSelectedElement } from '.'

/** Safety net: stop Generation Wait if Pending Generation Marker remains after this. */
export const GENERATION_WAIT_TIMEOUT_MS = 90_000

interface WsResponse extends WsResponseBase {
  content?: ContentType
}

const useWebsocketFullScreenLoader = () => {
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const { sendWebsocketMessage } = useWebsocketMessage()

  const [isLoading, setIsLoading] = useState(false)
  const [action, setAction] = useState<NLGAction>()
  const [hasTimedOut, setHasTimedOut] = useState(false)
  const {
    lastJsonMessage,
    isConnectionReady,
  }: {
    lastJsonMessage: WsResponse
    isConnectionReady: boolean
  } = useWebsockets()

  const selectedElementId = useAppSelector(
    (state) => state.unifiedFlow.selectedElementId
  )

  const { content, refetch } = useContent()

  const { data: selectedElement } = useSelectedElement()
  const selectedRootElement = getRootUserSelectedElement(
    content,
    selectedElement?.element_id
  )

  useEffect(() => {
    if (content && lastJsonMessage) {
      const generateGlobalBriefWsTopic = getWsTopic({
        action: 'generateGlobalBrief',
        data: { contentId: content._id },
      })
      if (lastJsonMessage.topics?.includes(generateGlobalBriefWsTopic)) {
        if (lastJsonMessage.status === 'success') {
          queryClient.setQueryData(
            contentKeys.content(content._id),
            lastJsonMessage.content
          )
          sendWebsocketMessage({
            action: 'generateGlobalBrief',
            data: { contentId: content._id },
            subscriptionAction: 'unsubscribe',
          })
        } else if (lastJsonMessage.status === 'error' && content) {
          queryClient.invalidateQueries({
            queryKey: contentKeys.content(content._id),
          })
          errorToast(lastJsonMessage.errorMessage)
          dispatch(toggleElementsDisabled(false))
        }
      }

      const generateElementBriefRegex = new RegExp(
        `content/${content._id}/element/([^/]+)/generateElementBrief`
      )
      const generateElementBriefTopic = lastJsonMessage.topics?.find((topic) =>
        generateElementBriefRegex.test(topic)
      )

      if (generateElementBriefTopic) {
        if (lastJsonMessage.status === 'success') {
          const wsTopicElementId = generateElementBriefTopic?.match(
            generateElementBriefRegex
          )?.[1]
          queryClient.setQueryData(
            contentKeys.content(content._id),
            lastJsonMessage.content
          )
          if (wsTopicElementId) {
            sendWebsocketMessage({
              action: 'generateElementBrief',
              data: {
                contentId: content._id,
                elementId: Number(wsTopicElementId),
              },
              subscriptionAction: 'unsubscribe',
            })
          }
        } else if (lastJsonMessage.status === 'error' && content) {
          queryClient.invalidateQueries({
            queryKey: contentKeys.content(content._id),
          })
          errorToast(lastJsonMessage.errorMessage)
          dispatch(toggleElementsDisabled(false))
        }
      }

      const generateElementVariantsRegex = new RegExp(
        `content/${content._id}/element/([^/]+)/generateElementVariants`
      )
      const generateElementVariantsTopic = lastJsonMessage.topics?.find(
        (topic) => generateElementVariantsRegex.test(topic)
      )

      if (generateElementVariantsTopic) {
        if (lastJsonMessage.status === 'success') {
          const wsTopicElementId = generateElementVariantsTopic?.match(
            generateElementVariantsRegex
          )?.[1]
          queryClient.setQueryData(
            contentKeys.content(content._id),
            lastJsonMessage.content
          )
          if (wsTopicElementId) {
            sendWebsocketMessage({
              action: 'generateElementVariants',
              data: {
                contentId: content._id,
                elementId: Number(wsTopicElementId),
              },
              subscriptionAction: 'unsubscribe',
            })
            if (
              stringToNumber(wsTopicElementId) &&
              (selectedElementId === BRIEF_ELEMENT_ID ||
                selectedElementId === undefined)
            ) {
              dispatch(
                selectElement({ elementId: stringToNumber(wsTopicElementId) })
              )
            }
          }
        } else if (lastJsonMessage.status === 'error' && content) {
          queryClient.invalidateQueries({
            queryKey: contentKeys.content(content._id),
          })
          errorToast(
            'Variants generation for one template was unsuccessful. Please try again.'
          )
          dispatch(toggleElementsDisabled(false))
        }
      }
    }
    // Respond only to lastJsonMessage changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastJsonMessage])

  useEffect(() => {
    const waitState = getFullscreenGenerationWait(
      content,
      selectedRootElement,
      isConnectionReady
    )

    if (!waitState.isWaiting) {
      setIsLoading(false)
      setAction(undefined)
      if (hasTimedOut) {
        setHasTimedOut(false)
      }
      return
    }

    setAction(waitState.action)

    if (hasTimedOut) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
  }, [content, isConnectionReady, selectedRootElement, hasTimedOut])

  useEffect(() => {
    if (!isLoading || hasTimedOut) {
      return
    }

    let cancelled = false

    const timeoutId = setTimeout(async () => {
      const { data: refetchedContent } = await refetch()

      if (cancelled) {
        return
      }

      const waitState = getFullscreenGenerationWait(
        refetchedContent,
        selectedRootElement,
        isConnectionReady
      )

      if (waitState.isWaiting) {
        setHasTimedOut(true)
        setIsLoading(false)
        dispatch(toggleElementsDisabled(false))
      }
    }, GENERATION_WAIT_TIMEOUT_MS)

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [
    isLoading,
    hasTimedOut,
    refetch,
    selectedRootElement,
    isConnectionReady,
    dispatch,
  ])

  const onRetryGenerationWait = useCallback(() => {
    setHasTimedOut(false)
  }, [])

  return {
    isWebsocketLoading: isLoading,
    websocketAction: action,
    hasGenerationWaitTimedOut: hasTimedOut,
    onRetryGenerationWait,
  }
}

export default useWebsocketFullScreenLoader
